import type { UtilisationBand } from '../../../utils/utilisation';
import type { Consultant } from '../types';

/** A `ConsultantUtilisation` row from `/planning/team/utilisation` (the bits the dashboard reads). */
interface RawConsultantUtilisation {
  consultantId: string;
  utilisation: number;
  /** The server's band for `utilisation`. */
  band?: UtilisationBand;
  projectBreakdown?: { name: string; utilisation: number }[];
  onLeave?: boolean;
  leaveDays?: number;
  allocationCount?: number;
}

interface TeamUtilisationLike {
  underUtilised?: RawConsultantUtilisation[];
  utilised?: RawConsultantUtilisation[];
  overUtilised?: RawConsultantUtilisation[];
}

/**
 * Merge the team-utilisation rows (utilisation + leave/capacity context) onto
 * the consultant list by id. Extracted from `useDashboardData` to keep that
 * orchestrator hook within the 500-line budget. Consultants with no matching
 * row read 0% with no leave.
 */
export function mergeConsultantUtilisation(
  consultants: Consultant[],
  data: TeamUtilisationLike,
): Consultant[] {
  const byId = new Map<string, RawConsultantUtilisation>();
  for (const row of [
    ...(data.underUtilised ?? []),
    ...(data.utilised ?? []),
    ...(data.overUtilised ?? []),
  ]) {
    byId.set(row.consultantId, row);
  }

  return consultants.map((consultant) => {
    const row = byId.get(consultant.id);
    return {
      ...consultant,
      utilisation: row?.utilisation || 0,
      // Nobody booked at all is unassigned, as the server counts it.
      band: row?.band ?? 'unassigned',
      projectBreakdown: row?.projectBreakdown,
      onLeave: row?.onLeave ?? false,
      leaveDays: row?.leaveDays ?? 0,
      allocationCount: row?.allocationCount ?? 0,
    };
  });
}
